import {
  ControlCategories,
  ControlCategory,
  ControlCode,
  ControlCodes,
  Feature,
  SCMCategories,
  SCMCategory,
  ValidControlCategory,
  ValidControlCode,
  ValidSCMCategory,
} from "@theprelude/sdk";

export const flaggedControls: Array<{ control: ControlCode; flag: Feature }> = [
  // detect
  { control: ControlCodes.GITHUB, flag: "detect" },
  { control: ControlCodes.S3, flag: "detect" },
  { control: ControlCodes.SPLUNK, flag: "detect" },
  { control: ControlCodes.VECTR, flag: "detect" },
  // policy_evaluator
  { control: ControlCodes.AWS_SSM, flag: "policy_evaluator" },
  { control: ControlCodes.CROWDSTRIKE_IDENTITY, flag: "policy_evaluator" },
  { control: ControlCodes.DEFENDER_DISCOVERY, flag: "policy_evaluator" },
  { control: ControlCodes.EC2, flag: "policy_evaluator" },
  { control: ControlCodes.ENTRA, flag: "policy_evaluator" },
  { control: ControlCodes.INTUNE, flag: "policy_evaluator" },
  { control: ControlCodes.INTUNE_HOST_FIREWALL, flag: "policy_evaluator" },
  { control: ControlCodes.GMAIL, flag: "policy_evaluator" },
  { control: ControlCodes.GOOGLE_IDENTITY, flag: "policy_evaluator" },
  { control: ControlCodes.JAMF, flag: "policy_evaluator" },
  { control: ControlCodes.M365, flag: "policy_evaluator" },
  { control: ControlCodes.OKTA, flag: "policy_evaluator" },
  { control: ControlCodes.QUALYS, flag: "policy_evaluator" },
  { control: ControlCodes.QUALYS_DISCOVERY, flag: "policy_evaluator" },
  { control: ControlCodes.RAPID7, flag: "policy_evaluator" },
  { control: ControlCodes.RAPID7_DISCOVERY, flag: "policy_evaluator" },
  { control: ControlCodes.SERVICENOW, flag: "policy_evaluator" },
  { control: ControlCodes.TENABLE, flag: "policy_evaluator" },
  { control: ControlCodes.TENABLE_DISCOVERY, flag: "policy_evaluator" },
] as const;

export function getRFMDescription(control: ControlCode): string {
  switch (control) {
    case ControlCodes.CROWDSTRIKE: {
      return "Reduced Functionality Mode";
    }
    case ControlCodes.DEFENDER:
    case ControlCodes.DEFENDER_DISCOVERY: {
      return "Passive Mode";
    }
    case ControlCodes.SENTINELONE: {
      return "Operational State";
    }
    default: {
      return "Reduced Functionality Mode";
    }
  }
}

const ValidControlMappings: Record<ValidControlCode, ValidControlCategory> = {
  [ControlCodes.AWS_SSM]: ControlCategories.ASSET_MANAGER,
  [ControlCodes.AZURE_VM]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.CROWDSTRIKE]: ControlCategories.XDR,
  [ControlCodes.CROWDSTRIKE_IDENTITY]: ControlCategories.IDENTITY,
  [ControlCodes.DEFENDER]: ControlCategories.XDR,
  [ControlCodes.DEFENDER_DISCOVERY]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.EC2]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.ENTRA]: ControlCategories.IDENTITY,
  [ControlCodes.GITHUB]: ControlCategories.PRIVATE_REPO,
  [ControlCodes.GMAIL]: ControlCategories.EMAIL,
  [ControlCodes.GOOGLE_IDENTITY]: ControlCategories.IDENTITY,
  [ControlCodes.INTUNE]: ControlCategories.ASSET_MANAGER,
  [ControlCodes.INTUNE_HOST_FIREWALL]: ControlCategories.HOST_FIREWALL,
  [ControlCodes.JAMF]: ControlCategories.ASSET_MANAGER,
  [ControlCodes.M365]: ControlCategories.EMAIL,
  [ControlCodes.OKTA]: ControlCategories.IDENTITY,
  [ControlCodes.QUALYS]: ControlCategories.VULN_MANAGER,
  [ControlCodes.QUALYS_DISCOVERY]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.RAPID7]: ControlCategories.VULN_MANAGER,
  [ControlCodes.RAPID7_DISCOVERY]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.S3]: ControlCategories.SIEM,
  [ControlCodes.SENTINELONE]: ControlCategories.XDR,
  [ControlCodes.SERVICENOW]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.SPLUNK]: ControlCategories.SIEM,
  [ControlCodes.TENABLE]: ControlCategories.VULN_MANAGER,
  [ControlCodes.TENABLE_DISCOVERY]: ControlCategories.DISCOVERED_DEVICES,
  [ControlCodes.VECTR]: ControlCategories.SIEM,
};

const ControlMappings: Record<ControlCode, ControlCategory> = {
  ...ValidControlMappings,
  [ControlCodes.INVALID]: ControlCategories.ASSET_MANAGER,
  [ControlCodes.NONE]: ControlCategories.ASSET_MANAGER,
};

type ValidSCMMappedControlCategory = Exclude<
  ValidControlCategory,
  | typeof ControlCategories.CLOUD
  | typeof ControlCategories.NETWORK
  | typeof ControlCategories.PRIVATE_REPO
  | typeof ControlCategories.SIEM
>;

const ValidCategoryMappings: Record<
  ValidSCMMappedControlCategory,
  ValidSCMCategory
> = {
  [ControlCategories.ASSET_MANAGER]: SCMCategories.ENDPOINT,
  [ControlCategories.DISCOVERED_DEVICES]: SCMCategories.ENDPOINT,
  [ControlCategories.EMAIL]: SCMCategories.INBOX,
  [ControlCategories.HOST_FIREWALL]: SCMCategories.ENDPOINT,
  [ControlCategories.IDENTITY]: SCMCategories.USER,
  [ControlCategories.VULN_MANAGER]: SCMCategories.ENDPOINT,
  [ControlCategories.XDR]: SCMCategories.ENDPOINT,
};

const CategoryMappings: Record<ControlCategory, SCMCategory> = {
  ...ValidCategoryMappings,
  [ControlCategories.INVALID]: SCMCategories.INVALID,
  [ControlCategories.CLOUD]: SCMCategories.NONE,
  [ControlCategories.NETWORK]: SCMCategories.NONE,
  [ControlCategories.PRIVATE_REPO]: SCMCategories.NONE,
  [ControlCategories.SIEM]: SCMCategories.NONE,
};

const ControlNames: Record<ControlCode, string> = {
  [ControlCodes.INVALID]: "",
  [ControlCodes.NONE]: "No EDR",
  [ControlCodes.AWS_SSM]: "Amazon SSM",
  [ControlCodes.AZURE_VM]: "Azure VM",
  [ControlCodes.CROWDSTRIKE]: "CrowdStrike",
  [ControlCodes.CROWDSTRIKE_IDENTITY]: "CrowdStrike Identity Protection",
  [ControlCodes.DEFENDER]: "Microsoft Defender",
  [ControlCodes.DEFENDER_DISCOVERY]: "Microsoft Defender",
  [ControlCodes.EC2]: "Amazon EC2",
  [ControlCodes.ENTRA]: "Microsoft Entra ID",
  [ControlCodes.GITHUB]: "GitHub",
  [ControlCodes.GMAIL]: "Gmail",
  [ControlCodes.GOOGLE_IDENTITY]: "Google Cloud Identity Platform",
  [ControlCodes.INTUNE]: "Microsoft Intune",
  [ControlCodes.JAMF]: "Jamf",
  [ControlCodes.M365]: "Microsoft 365",
  [ControlCodes.OKTA]: "Okta",
  [ControlCodes.QUALYS]: "Qualys",
  [ControlCodes.QUALYS_DISCOVERY]: "Qualys",
  [ControlCodes.RAPID7]: "Rapid7",
  [ControlCodes.RAPID7_DISCOVERY]: "Rapid7",
  [ControlCodes.SERVICENOW]: "ServiceNow",
  [ControlCodes.SPLUNK]: "Splunk",
  [ControlCodes.S3]: "Amazon S3",
  [ControlCodes.SENTINELONE]: "SentinelOne",
  [ControlCodes.VECTR]: "VECTR",
  [ControlCodes.TENABLE]: "Tenable",
  [ControlCodes.TENABLE_DISCOVERY]: "Tenable",
  [ControlCodes.INTUNE_HOST_FIREWALL]: "Microsoft Intune",
};

const ControlCategoryNames: Record<ControlCategory, string> = {
  [ControlCategories.ASSET_MANAGER]: "Endpoint Management",
  [ControlCategories.CLOUD]: "Cloud",
  [ControlCategories.DISCOVERED_DEVICES]: "Discovered Devices",
  [ControlCategories.EMAIL]: "Email",
  [ControlCategories.IDENTITY]: "Identity Provider",
  [ControlCategories.INVALID]: "",
  [ControlCategories.NETWORK]: "Network",
  [ControlCategories.PRIVATE_REPO]: "Private Repository",
  [ControlCategories.SIEM]: "SIEM",
  [ControlCategories.VULN_MANAGER]: "Vulnerability Management",
  [ControlCategories.XDR]: "EDR",
  [ControlCategories.HOST_FIREWALL]: "Host Firewall",
};

const SCMCategoryNames: Record<SCMCategory, string> = {
  [SCMCategories.ENDPOINT]: "Endpoint",
  [SCMCategories.INBOX]: "Inbox",
  [SCMCategories.INVALID]: "",
  [SCMCategories.NONE]: "",
  [SCMCategories.USER]: "User",
};

export const getControlCategory = (control: ControlCode): ControlCategory => {
  return ControlMappings[control];
};

export const getValidControlCategory = (
  control: ValidControlCode,
): ValidControlCategory => {
  return ValidControlMappings[control];
};

export const getSCMCategory = (control: ControlCode): SCMCategory => {
  return CategoryMappings[getControlCategory(control)];
};

export const getControlCategoryName = (category: ControlCategory) => {
  if (category === undefined || category === ControlCategories.INVALID) {
    return "";
  }
  return ControlCategoryNames[category];
};

export const getSCMCategoryName = (category: SCMCategory) => {
  if (category === undefined) {
    return "";
  }
  return SCMCategoryNames[category];
};

export const getControlName = (control: ControlCode) => {
  return ControlNames[control];
};

export const controlOptions: { id: ValidControlCode; name: string }[] =
  Object.values(ControlCodes)
    .filter(isValidControl)
    .map((value) => ({
      id: value,
      name: getControlName(value),
    }));

export function isValidControl(
  control: ControlCode,
): control is ValidControlCode {
  return control !== ControlCodes.NONE && control !== ControlCodes.INVALID;
}
export function isEndpointControl(control: ControlCode) {
  return (
    isEndpointManagementControl(control) ||
    isDiscoveredDevicesControl(control) ||
    isDefensiveControl(control) ||
    isVulnerabilityManagementControl(control) ||
    isHostFirewallControl(control)
  );
}

export function isDefensiveControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.CROWDSTRIKE
  | typeof ControlCodes.DEFENDER
  | typeof ControlCodes.SENTINELONE {
  return (
    control === ControlCodes.CROWDSTRIKE ||
    control === ControlCodes.DEFENDER ||
    control === ControlCodes.SENTINELONE
  );
}

export function isDataControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.SPLUNK
  | typeof ControlCodes.S3
  | typeof ControlCodes.VECTR {
  return (
    control === ControlCodes.SPLUNK ||
    control === ControlCodes.S3 ||
    control === ControlCodes.VECTR
  );
}

export function isHostFirewallControl(
  control: ControlCode,
): control is typeof ControlCodes.INTUNE_HOST_FIREWALL {
  return control === ControlCodes.INTUNE_HOST_FIREWALL;
}

export const isSIEMControl = isDataControl;

export function isEndpointManagementControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.AWS_SSM
  | typeof ControlCodes.INTUNE
  | typeof ControlCodes.JAMF {
  return (
    control === ControlCodes.AWS_SSM ||
    control === ControlCodes.INTUNE ||
    control === ControlCodes.JAMF
  );
}

export function isFirewallControl(
  control: ControlCode,
): control is typeof ControlCodes.INTUNE_HOST_FIREWALL {
  return control === ControlCodes.INTUNE_HOST_FIREWALL;
}

export function isVulnerabilityManagementControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.TENABLE
  | typeof ControlCodes.QUALYS
  | typeof ControlCodes.RAPID7 {
  return (
    control === ControlCodes.TENABLE ||
    control === ControlCodes.QUALYS ||
    control === ControlCodes.RAPID7
  );
}

export function isDiscoveredDevicesControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.DEFENDER_DISCOVERY
  | typeof ControlCodes.EC2
  | typeof ControlCodes.SERVICENOW
  | typeof ControlCodes.AZURE_VM
  | typeof ControlCodes.TENABLE_DISCOVERY
  | typeof ControlCodes.QUALYS_DISCOVERY
  | typeof ControlCodes.RAPID7_DISCOVERY {
  return (
    control == ControlCodes.DEFENDER_DISCOVERY ||
    control == ControlCodes.EC2 ||
    control === ControlCodes.SERVICENOW ||
    control === ControlCodes.AZURE_VM ||
    control === ControlCodes.TENABLE_DISCOVERY ||
    control === ControlCodes.QUALYS_DISCOVERY ||
    control === ControlCodes.RAPID7_DISCOVERY
  );
}

export function isEmailControl(
  control: ControlCode,
): control is typeof ControlCodes.GMAIL | typeof ControlCodes.M365 {
  return control === ControlCodes.GMAIL || control === ControlCodes.M365;
}

export function isIdentityProviderControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.CROWDSTRIKE_IDENTITY
  | typeof ControlCodes.ENTRA
  | typeof ControlCodes.GOOGLE_IDENTITY
  | typeof ControlCodes.OKTA {
  return (
    control === ControlCodes.CROWDSTRIKE_IDENTITY ||
    control === ControlCodes.ENTRA ||
    control === ControlCodes.GOOGLE_IDENTITY ||
    control === ControlCodes.OKTA
  );
}

export function isPrivateRepoControl(
  control: ControlCode,
): control is typeof ControlCodes.GITHUB {
  return control === ControlCodes.GITHUB;
}

export function isSCMControl(control: ControlCode) {
  const scmCategory = getSCMCategory(control);
  return (
    scmCategory !== SCMCategories.INVALID && scmCategory !== SCMCategories.NONE
  );
}

export const getCategoryControls = (
  category: ControlCategory,
): ValidControlCode[] => {
  const entries = Object.entries(ValidControlMappings);

  return entries.reduce(
    (result: ValidControlCode[], [controlCode, controlCategory]) => {
      if (category === controlCategory) {
        result.push(Number(controlCode) as ValidControlCode);
      }
      return result;
    },
    [],
  );
};

export const getSCMControls = (
  category: ValidSCMCategory,
): ValidControlCode[] => {
  return getSCMControlCategories(category).flatMap(getCategoryControls);
};

export const getSCMCategoryFromControlCategory = (
  controlCategory: ControlCategory,
): SCMCategory => CategoryMappings[controlCategory];

export const getSCMControlCategories = (
  category: ValidSCMCategory,
): ValidControlCategory[] => {
  const entries = Object.entries(ValidCategoryMappings);

  const controlCategories = entries.reduce(
    (result: ValidControlCategory[], [controlCategory, scmCategory]) => {
      if (category === scmCategory) {
        result.push(Number(controlCategory) as ValidSCMCategory);
      }
      return result;
    },
    [],
  );

  return controlCategories;
};
